import React, { useEffect } from 'react';
import { ArrowRight, ShieldCheck, Microscope, Database, Verified } from 'lucide-react';
import SEO from './SEO';

interface Props {
  onNavigate: (view: any, sectionId?: string) => void;
}

const Certifications: React.FC<Props> = ({ onNavigate }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-24 pb-16 bg-white">
      <SEO 
        title="Our Certifications & Quality | BuildMySupplement" 
        description="We are a GMP-certified supplement manufacturer committed to uncompromising quality, FDA compliance, and third-party testing for your peace of mind." 
      />
      
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        <div className="max-w-4xl mx-auto text-center">
           <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-6">
            <span className="text-blue-700 text-xs font-bold uppercase tracking-widest flex items-center">
              <ShieldCheck className="w-4 h-4 mr-2" /> Uncompromising Quality
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 tracking-tight mb-8 leading-tight">
            Safety and Compliance Are Not <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Optional.</span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-3xl mx-auto">
            When your followers trust you enough to ingest a product you recommend, that trust must be protected by rigorous, uncompromising scientific standards.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-lg text-gray-700 space-y-12">
        
        {/* GMP Section */}
        <div className="bg-gray-50 p-8 md:p-12 rounded-3xl border border-gray-100 relative overflow-hidden">
           <ShieldCheck className="absolute -right-10 -top-10 w-64 h-64 text-gray-200/50 pointer-events-none" />
           <div className="relative z-10">
              <h2 className="text-3xl font-extrabold text-gray-900 mb-6 flex items-center"><Verified className="w-8 h-8 text-blue-600 mr-3" /> What is GMP Certification?</h2>
              <p className="mb-4 leading-relaxed">
                GMP stands for <strong>Good Manufacturing Practice</strong>. It is a strict system mandated by the U.S. Food and Drug Administration (FDA) that ensures products are consistently produced and controlled according to the highest quality standards.
              </p>
              <p className="mb-4 leading-relaxed">
                A GMP certification is not a one-time ribbon; it is an ongoing, exhausting commitment to excellence. It covers every single aspect of production, from the starting materials, premises, and equipment, down to the training and personal hygiene of the staff. It requires detailed, written procedures demonstrating exactly how our facility prevents cross-contamination, mix-ups, and errors at every step.
              </p>
              
              <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Why GMP Matters for Your Brand</h3>
              <p className="mb-4 leading-relaxed">
                Without GMP, you are essentially gambling with your customers' health. In non-GMP facilities, a pre-workout powder might accidentally be contaminated with allergens from a previous run, or the dosage of an active ingredient might violently fluctuate from bottle to bottle.
              </p>
              <p className="mb-4 leading-relaxed">
                By manufacturing your products in our GMP-certified facility, you guarantee your customers that what is on the label is exactly what is in the bottle—nothing more, nothing less. It gives you the legal and moral authority to stand behind your brand with total conviction.
              </p>
           </div>
        </div>

        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">Beyond GMP: The Pillars of Our Quality Control</h2>
          <div className="space-y-8 mt-8">
             <div className="flex">
                <div className="flex-shrink-0 mt-1">
                   <Microscope className="w-8 h-8 text-blue-600" />
                </div>
                <div className="ml-5">
                   <h3 className="text-xl font-bold text-gray-900">Third-Party Analytical Testing</h3>
                   <p className="mt-2 text-gray-700 leading-relaxed">We utilize ISO-accredited independent laboratories to test raw materials upon arrival and finished products before they ship. They test for identity (is this botanical what the supplier claims it is?), purity (is it free of heavy metals, mold, and microbes?), and potency (are the active ingredient levels correct?).</p>
                </div>
             </div>
             
             <div className="flex">
                <div className="flex-shrink-0 mt-1">
                   <Database className="w-8 h-8 text-blue-600" />
                </div>
                <div className="ml-5">
                   <h3 className="text-xl font-bold text-gray-900">Documented Chain of Custody</h3>
                   <p className="mt-2 text-gray-700 leading-relaxed">Transparency is built into our software. Every batch of product comes with a Certificate of Analysis (CoA) and complete batch records. If a customer ever asks a question about a specific bottle's origin, we can trace it back to the exact day, machine, and raw material lot it was produced from.</p>
                </div>
             </div>
          </div>
        </div>

      </section>

      {/* CTA */}
      <section className="bg-blue-900 text-white py-20 mt-12 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-4xl font-extrabold mb-6">Build a Brand You Can Stand Behind.</h2>
          <p className="text-xl text-blue-100 mb-10">
            Partner with a manufacturer that takes your customers' safety as seriously as you do.
          </p>
          <button 
            onClick={() => onNavigate('contact')}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-blue-900 bg-white rounded-full hover:bg-gray-100 transition-all duration-200"
          >
            Contact Our Team <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>

    </div>
  );
};

export default Certifications;
